// At the top
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import axios from "axios";
import "./ChatApp.css";
import { API_BASE_URL } from "../config";
import up from "../assets/up.png";
import qs from "qs";
const ChatApp = ({
  userId,
  fetchCredits,
  setIsLocked,
  setShowOptions,
  isLocked,
  freeResponsesLeft,
  setFreeResponsesLeft,
  handleLogout = () => {},
}) => {
  const navigate = useNavigate();
  const chatEndRef = useRef(null);

  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [credits, setCredits] = useState(100);
  const [scheduleMode, setScheduleMode] = useState(false);

  const numericUserId = Number(userId);

  const getCurrentMessages = () => {
    const chat = chats.find((chat) => chat.id === selectedChatId);
    return chat ? chat.messages : [];
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chats, isTyping]);

  const addMessageToChat = (message, chatId = selectedChatId) => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === chatId
          ? { ...chat, messages: [...chat.messages, message] }
          : chat
      )
    );
  };

  // npm install qs

  const handleRenameChat = async (chatId, newTitle) => {
    try {
      await axios.put(
        `${API_BASE_URL}/rename-chat/${numericUserId}/${chatId}`,
        qs.stringify({ new_name: newTitle }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === chatId ? { ...chat, title: newTitle } : chat
        )
      );
    } catch (err) {
      console.error("❌ Rename error", err);
    }
  };

  const handleDeleteChat = async (chatId) => {
    try {
      await axios.delete(`${API_BASE_URL}/delete-chat/${chatId}`);
      setChats((prevChats) => prevChats.filter((chat) => chat.id !== chatId));
      if (selectedChatId === chatId) {
        const nextChat = chats.find((chat) => chat.id !== chatId);
        setSelectedChatId(nextChat?.id || null);
      }
    } catch (err) {
      console.error("❌ Delete error", err);
    }
  };

  const createInitialChat = async () => {
    const problem = localStorage.getItem("user_problem");
    const newChatId = Date.now();
    const introMessages = [];

    if (problem) {
      introMessages.push({
        sender: "bot",
        text: `✅ Got it! You mentioned: "${problem}". Let me analyze that...`,
      });
    }

    const initialChat = {
      id: newChatId,
      title: problem ? "Problem Analysis" : "New Chat",
      messages: introMessages,
    };

    setChats((prev) => [...prev, initialChat]);
    setSelectedChatId(newChatId);
    localStorage.setItem(`chatId-${numericUserId}`, newChatId);

    try {
      // Save the initial chat on backend
      await axios.post(`${API_BASE_URL}/create-chat`, {
        user_id: numericUserId,
        chat_id: newChatId,
        title: initialChat.title,
        message: problem || "",
        sender: "user",
      });
    } catch (err) {
      console.error("❌ Failed to create chat on server", err);
    }

    if (problem) {
      localStorage.removeItem("user_problem");
      setIsTyping(true);

      try {
        const response = await fetch(`${API_BASE_URL}/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: numericUserId,
            message: problem,
            chat_id: newChatId,
          }),
        });

        if (!response.ok || !response.body) {
          throw new Error("Streaming failed");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let botText = "";

        // Add empty placeholder message first
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === newChatId
              ? {
                  ...chat,
                  messages: [
                    ...chat.messages,
                    {
                      sender: "bot",
                      text: "",
                    },
                  ],
                }
              : chat
          )
        );

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          botText += chunk;

          // Update last message progressively
          setChats((prevChats) =>
            prevChats.map((chat) =>
              chat.id === newChatId
                ? {
                    ...chat,
                    messages: chat.messages.map((msg, i) =>
                      i === chat.messages.length - 1 && msg.sender === "bot"
                        ? { ...msg, text: botText }
                        : msg
                    ),
                  }
                : chat
            )
          );
        }

        // Final update (ensures complete message)
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === newChatId
              ? {
                  ...chat,
                  messages: chat.messages.map((msg, i) =>
                    i === chat.messages.length - 1 && msg.sender === "bot"
                      ? { ...msg, text: botText }
                      : msg
                  ),
                }
              : chat
          )
        );

        // Lock message handling
        if (botText.includes("🔒")) {
          setIsLocked(true);
          setShowOptions(true);
        }

        const updatedCredits = await fetchCredits(numericUserId);
        setCredits(updatedCredits);
      } catch (err) {
        console.error("❌ Streaming failed", err);
        addMessageToChat({
          sender: "bot",
          text: "❌ Something went wrong while processing your issue.",
        });
      } finally {
        setIsTyping(false);
      }
    }
  };

  const fetchPreviousChats = async () => {
    const storedChatId = localStorage.getItem(`chatId-${numericUserId}`);

    try {
      const res = await axios.get(`${API_BASE_URL}/get-chats`, {
        params: { user_id: numericUserId },
      });

      if (res.data?.length > 0) {
        setChats(res.data);

        // Try selecting stored chat first
        const matchedChat = res.data.find((chat) => chat.id == storedChatId);
        if (matchedChat) {
          setSelectedChatId(matchedChat.id);
        } else {
          setSelectedChatId(res.data[0].id);
          localStorage.setItem(`chatId-${numericUserId}`, res.data[0].id);
        }
      } else {
        createInitialChat();
      }
    } catch {
      createInitialChat();
    }
  };

  useEffect(() => {
    if (numericUserId) {
      fetchPreviousChats();
      fetchCredits(numericUserId).then(setCredits);
    }
  }, [userId]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const cleanedInput = input.trim();
    const currentChatId = selectedChatId;

    // Show user message immediately
    addMessageToChat({ sender: "user", text: cleanedInput }, currentChatId);
    setInput("");
    setIsTyping(true);

    // Check for locking
    if ((credits <= 0 && freeResponsesLeft <= 0) || isLocked) {
      addMessageToChat(
        {
          sender: "bot",
          text: "🔒 To continue, you can either buy credits or schedule a meeting.",
        },
        currentChatId
      );
      setShowOptions(true);
      setIsTyping(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: numericUserId,
          message: cleanedInput,
          chat_id: currentChatId,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error("Streaming failed");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let botText = "";

      // Add placeholder bot message first
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === currentChatId
            ? {
                ...chat,
                messages: [...chat.messages, { sender: "bot", text: "" }],
              }
            : chat
        )
      );

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        botText += chunk;

        // Update the last bot message progressively
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === currentChatId
              ? {
                  ...chat,
                  messages: chat.messages.map((msg, i) =>
                    i === chat.messages.length - 1 && msg.sender === "bot"
                      ? { ...msg, text: botText }
                      : msg
                  ),
                }
              : chat
          )
        );
      }

      // Final check: if response has 🔒
      if (botText.includes("🔒")) {
        setIsLocked(true);
        setShowOptions(true);
      }

      const updatedCredits = await fetchCredits(numericUserId);
      setCredits(updatedCredits);
    } catch (err) {
      console.error("❌ Streaming failed", err);
      addMessageToChat(
        {
          sender: "bot",
          text: "❌ Something went wrong while processing your message.",
        },
        currentChatId
      );
    } finally {
      setIsTyping(false);
    }
  };

  const currentMessages = useMemo(() => {
    const chat = chats.find((chat) => chat.id === selectedChatId);
    return chat ? chat.messages : [];
  }, [chats, selectedChatId]);

  return (
    <div style={{ backgroundColor: "#121212", color: "#fff" }}>
      <Sidebar
        chats={chats}
        onNewChat={() => {
          const newChatId = Date.now();
          const newChat = { id: newChatId, title: "New Chat", messages: [] };
          setChats((prev) => [newChat, ...prev]);
          setSelectedChatId(newChatId);
          setScheduleMode(false);

          axios
            .post(`${API_BASE_URL}/create-chat`, {
              user_id: numericUserId,
              chat_id: newChatId,
              title: "New Chat",
            })
            .catch((err) => console.error("❌ Failed to save new chat", err));
        }}
        onSelectChat={(chatId) => setSelectedChatId(chatId)}
        selectedChatId={selectedChatId}
        onLogout={handleLogout}
        onRenameChat={handleRenameChat}
        onDeleteChat={handleDeleteChat}
      />

      <div className="chat-app">
        <div className="chat-container">
          <div className="messages">
            {currentMessages.length === 0 && !isTyping && (
              <div className="message bot">No messages yet.</div>
            )}

            {currentMessages.map((msg, index) => (
              <div key={index} className={`message ${msg.sender}`}>
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: (props) => (
                      <h1 className="message-heading" {...props} />
                    ),
                    h2: (props) => (
                      <h2 className="message-heading" {...props} />
                    ),
                    h3: (props) => (
                      <h3 className="message-subheading" {...props} />
                    ),
                    ul: (props) => <ul className="message-list" {...props} />,
                    li: (props) => (
                      <li className="message-list-item" {...props} />
                    ),
                    p: (props) => (
                      <p className="message-paragraph" {...props} />
                    ),
                    code: ({ inline, children, ...props }) =>
                      inline ? (
                        <code className="inline-code" {...props}>
                          {children}
                        </code>
                      ) : (
                        <pre className="code-block">
                          <code {...props}>{children}</code>
                        </pre>
                      ),
                    blockquote: (props) => (
                      <blockquote className="blockquote" {...props} />
                    ),
                    table: (props) => (
                      <div className="table-wrapper">
                        <table className="markdown-table" {...props} />
                      </div>
                    ),
                  }}
                >
                  {msg.text}
                </ReactMarkdown>
              </div>
            ))}

            {isTyping && (
              <div className="message bot typing">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="input-bar">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                scheduleMode
                  ? "Enter date and time (e.g. YYYY-MM-DD HH:MM)"
                  : "Ask anything"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <img className="send" src={up} alt="send" onClick={handleSend} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatApp;
